// Firebase configuration and messaging setup
import { initializeApp, getApps } from 'firebase/app';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
let app;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Check if push notifications are supported
export function isPushSupported() {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!('Notification' in window)) return false;
  
  // Check if running in standalone PWA mode on iOS
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone === true;
  
  // iOS Safari (not PWA) doesn't support push
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  if (isIOS && isSafari && !isStandalone) {
    return false;
  }
  
  return true;
}

// Get messaging instance lazily (only when needed)
let messagingInstance = null;

async function getMessagingInstance() {
  if (!isPushSupported()) {
    return null;
  }
  
  if (messagingInstance) {
    return messagingInstance;
  }
  
  try {
    const { getMessaging } = await import('firebase/messaging');
    messagingInstance = getMessaging(app);
    return messagingInstance;
  } catch (err) {
    console.log('Firebase messaging not supported:', err.message);
    return null;
  }
}

// Request notification permission and get FCM token
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    console.log('Push notifications not supported on this browser');
    return { success: false, error: 'unsupported', token: null };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'denied', token: null };
    }
    
    const messaging = await getMessagingInstance();
    if (!messaging) {
      return { success: false, error: 'unsupported', token: null };
    }
    
    const { getToken } = await import('firebase/messaging');
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
    });
    
    if (token) {
      // Save token to backend
      try {
        const { API_URL: apiUrl } = await import('../config/api');
        const response = await fetch(`${apiUrl}/api/notifications/fcm-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ fcm_token: token })
        });
        
        if (response.ok) {
          console.log('FCM token saved to backend');
          // Also save to localStorage for reference
          localStorage.setItem('fcm_token', token);
          return { success: true, error: null, token };
        } else {
          console.error('Failed to save FCM token to backend');
          return { success: false, error: 'save_failed', token };
        }
      } catch (saveErr) {
        console.error('Error saving FCM token:', saveErr);
        return { success: false, error: 'save_failed', token };
      }
    }
    
    return { success: false, error: 'no_token', token: null };
  } catch (err) {
    console.error('Error getting notification permission:', err);
    return { success: false, error: err.message, token: null };
  }
}

// Listen for foreground messages
export async function onForegroundMessage(callback) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  
  try {
    const { onMessage } = await import('firebase/messaging');
    return onMessage(messaging, (payload) => {
      console.log('Foreground message:', payload);
      callback(payload);
    });
  } catch (err) {
    console.error('Error setting up foreground messages:', err);
    return () => {};
  }
}

export { app };
